import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "../utils/api/authApi";
import { GET_WAREHOUSE_ITEM, WAREHOUSE_LABELS_BATCH } from "../enums/ApiPaths";
import { fetchWarehouseItemQrSvg } from "../enums/queryFunction/Fetch";
import { WAREHOUSE_ITEM_QR_CODE } from "../enums/QueryKeys";
import logger from "../utils/logger";

export function useWarehouseItemQrCodeSvg(itemId) {
  return useQuery({
    queryKey: [WAREHOUSE_ITEM_QR_CODE, itemId],
    queryFn: () => fetchWarehouseItemQrSvg(itemId),
    enabled: !!itemId,
    retry: false,
  });
}

export function useDownloadWarehouseItemLabel() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const downloadLabel = async (itemId, { nameLabel, priceLabel, codeLabel }) => {
    if (!itemId) return false;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `${GET_WAREHOUSE_ITEM}${itemId}/label`,
        {
          params: { nameLabel, priceLabel, codeLabel },
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `warehouse-item-label-${itemId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (err) {
      logger.error("Failed to download warehouse item label:", err);
      setError(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { downloadLabel, isLoading, error };
}

export function useDownloadWarehouseLabelsBatch() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const downloadBatch = async (items, format, { nameLabel, priceLabel, codeLabel }) => {
    if (!items || items.length === 0) return false;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(
        WAREHOUSE_LABELS_BATCH,
        {
          items: items.map(({ itemId, quantity }) => ({ itemId, quantity })),
          format,
          nameLabel,
          priceLabel,
          codeLabel,
        },
        {
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "warehouse-item-labels.pdf");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (err) {
      logger.error("Failed to download warehouse labels batch:", err);
      setError(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { downloadBatch, isLoading, error };
}
